import { useState } from 'react';
import {
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
  Alert,

  Fade,

  useTheme,
  alpha,
  IconButton,
  InputAdornment,

} from '@mui/material';
import {
  Redeem as RedeemIcon,
  Discount as DiscountIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  LocalOffer as OfferIcon,
} from '@mui/icons-material';
import { callAPI, Method } from '../../../commonFunctions/CommonFunctions';
import { RewardDetails } from './RewardDetails';

export default function RedeemReward() {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [reward, setReward] = useState<any>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  const handleCheck = async () => {
    if (!code.trim()) {
      setError('Please enter a reward code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setReward(null);

    try {
      const response: any = await callAPI(`events/reward/verify`, Method.POST, { code });
      setReward(response);
      setSuccess('Reward code verified successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid or expired code.');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    setLoading(true);
    setError('');

    try {
      await callAPI(`events/reward/redeem`, Method.POST, { code });
      setReward({ ...reward, status: 'Redeemed' });
      setSuccess('Reward redeemed successfully! 🎉');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Redeem failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetForm = () => {
    setCode('');
    setReward(null);
    setError('');
    setSuccess('');
  };

  // Format card number to show only last 4 digits
  const formatCardNumber = (cardNumber: string) => {
    if (!cardNumber) return 'N/A';
    return `**** **** **** ${cardNumber.slice(-4)}`;
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4, p: 2 }}>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'white',
                p: 1.5,
                borderRadius: '50%',
                mb: 2
              }}
            >
              <RedeemIcon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" component="h1" fontWeight="700" gutterBottom>
              Redeem Your Reward
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Enter your reward code below to claim your special offer
            </Typography>
          </Box>

          {/* Input Field */}
          <TextField
            label="Reward Code"
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={loading || reward?.status === 'Redeemed'}
            placeholder="e.g. SUMMER25-OFFER"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DiscountIcon color="primary" />
                </InputAdornment>
              ),
              endAdornment: code && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopyCode}
                    size="small"
                    color={copied ? "success" : "default"}
                  >
                    {copied ? <CheckCircleIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ mb: 2 }}
            onKeyDown={(e) => e.key === 'Enter' && handleCheck()}
          />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleCheck}
              disabled={!code.trim() || loading || reward?.status === 'Redeemed'}
              startIcon={loading ? <CircularProgress size={16} /> : <OfferIcon />}
              sx={{ py: 1.5, borderRadius: 2 }}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>

            {reward && (
              <Button
                variant="outlined"
                onClick={resetForm}
                sx={{ borderRadius: 2, minWidth: 'auto', px: 2 }}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* Messages */}
          <Box sx={{ minHeight: 70 }}>
            <Fade in={!!error}>
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            </Fade>

            <Fade in={!!success}>
              <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                {success}
              </Alert>
            </Fade>
          </Box>

          {/* Reward Details */}
          <RewardDetails
            handleRedeem={handleRedeem} loading={loading} reward={reward} />
        </CardContent>
      </Card>
    </Box>
  );
}